// Which `TaskFailureKind`s are the MACHINE's or the infrastructure's, never the ask's: the run
// could not have told us anything about the request. `cron.ask_close_loop` reads it: a
// generation that died on one of these spends nothing of the bound that closes a real issue
// `not planned` (`apps/worker/src/cron/ask-close-loop-charge.ts`). Prod, 2026-11-01: 832 of
// 2,704 scheduled tasks did not land, so a wave of infrastructure faults would otherwise have
// closed real issues.
//
// BY FAMILY, then by name:
//   - every PROVIDER kind (a model or its key failed) and every BOX kind (the machine, its
//     checkout or its services failed), by construction: a new row in either family joins;
//   - from the two mixed families, the kinds below, each a run that said nothing about the ask:
//     a box or runner fault (`blamesBox`), a cascade from a task that is not this one, a
//     credential, an assignment or a claim we failed, an event our own plane lost.
//
// `failure-machine.test.ts` pins both sides of the mixed families, so a new tree or run kind
// fails until someone decides it. What stays charged and why: `verify_failed`, `no_pr_opened`,
// `run_timeout`, `run_futile`, `run_looping` and their neighbours are the work failing, the
// deterministic failure the close bound exists for.

import { BOX_FAILURE_KINDS } from './failure-kinds/box';
import { PROVIDER_FAILURE_KINDS } from './failure-kinds/provider';
import { RUN_FAILURE_KINDS } from './failure-kinds/run';
import { TREE_FAILURE_KINDS } from './failure-kinds/tree';

const MACHINE_TREE_KINDS = [
  TREE_FAILURE_KINDS.DependencyFailed,
  TREE_FAILURE_KINDS.PlanDependencyFailed,
  TREE_FAILURE_KINDS.GitAuth,
  TREE_FAILURE_KINDS.EnvelopeInvalid,
  TREE_FAILURE_KINDS.ClaimRollbackExhausted,
];

const MACHINE_RUN_KINDS = [
  RUN_FAILURE_KINDS.RunnerError,
  RUN_FAILURE_KINDS.ErrorUnclassified,
  RUN_FAILURE_KINDS.RunKilled,
  RUN_FAILURE_KINDS.SurveyAnswerLost,
  RUN_FAILURE_KINDS.ManifestGradingFailed,
  RUN_FAILURE_KINDS.ManifestEventFailed,
  RUN_FAILURE_KINDS.ServiceProbeEventFailed,
  RUN_FAILURE_KINDS.ScreenUnavailable,
  RUN_FAILURE_KINDS.ScreenKeyUnusable,
];

/** Every kind a machine or the infrastructure produced; the module header holds the rule. */
export const TASK_MACHINE_FAILURE_KINDS: ReadonlySet<string> = new Set(
  [
    ...Object.values(PROVIDER_FAILURE_KINDS),
    ...Object.values(BOX_FAILURE_KINDS),
    ...MACHINE_TREE_KINDS,
    ...MACHINE_RUN_KINDS,
  ].map((row) => row.value),
);

/** Takes the raw column: a value outside the vocabulary is not provably the machine's. */
export function taskFailureIsMachine(kind: string): boolean {
  return TASK_MACHINE_FAILURE_KINDS.has(kind);
}
